import React from "react";
import { useTranslation } from "react-i18next";
import { placeholderLabel } from "../../utility/placeholderLabel";
import GarageChargerImage from "resources/images/GarageChargerImage.png";
import { useNavigate } from "react-router-dom";
import {
  handleNameChange,
  handlePriceChange,
  handleSelectionChange,
  TextBoxSelection,
  TextBoxSelectionData,
} from "components/basic/textBoxSelection";
import { useEditMode } from "../../utility";
import WizardPageLayout from "../../components/WizardPageLayout";
import WizardSection from "../../components/WizardSection";
import WizardButton from "../../components/WizardButton";

const FORM_WIDTH = 440;

interface InstallationConfigurationProps {
  data: InstallationConfigData;
  setData: React.Dispatch<React.SetStateAction<InstallationConfigData>>;
}

export interface InstallationConfigData {
  cableLength: TextBoxSelectionData[];
  inclusions: TextBoxSelectionData[];
  addOnServices: TextBoxSelectionData[];
}

const InstallationConfiguration: React.FC<InstallationConfigurationProps> = ({
  data,
  setData,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("services");
  const { isEditMode, handleSave, handleCancel } = useEditMode(data, setData);

  return (
    <WizardPageLayout
      title={t("wizard.installation.title")}
      image={{
        src: GarageChargerImage,
        alt: t("wizard.image_alt"),
        side: "right",
      }}
      contentMaxWidth={FORM_WIDTH}
      actions={
        <>
          <WizardButton
            color="primary"
            onClick={() => {
              if (isEditMode) {
                navigate(`?currentPage=${handleCancel()}`);
              } else {
                navigate("?currentPage=chargerConfig");
              }
            }}
          >
            {t(isEditMode ? "wizard.cancel" : "wizard.back")}
          </WizardButton>

          <WizardButton
            color="warning"
            onClick={() =>
              isEditMode
                ? navigate(`?currentPage=${handleSave()}`)
                : navigate("?currentPage=paymentAndAssumptions")
            }
          >
            {t(isEditMode ? "wizard.save" : "wizard.next")}
          </WizardButton>
        </>
      }
    >
      <WizardSection title={t("wizard.installation.cable")}>
        {data.cableLength.map((element) => (
          <TextBoxSelection
            key={element.id}
            textBoxSelectionType={element.type}
            label={element.optionName}
            checked={element.isChecked}
            onSelectionChange={(checked) =>
              handleSelectionChange<TextBoxSelectionData>(
                setData,
                checked,
                "cableLength",
                element.id,
                element.duplicates,
              )
            }
            onPriceChange={(e) =>
              handlePriceChange(setData, element.id, e, "cableLength")
            }
            onNameChange={(e) =>
              handleNameChange(setData, element.id, e, "cableLength")
            }
            price={element.price}
            name={element.enteredName}
          />
        ))}
      </WizardSection>

      <WizardSection title={t("wizard.installation.inclusions")}>
        {data.inclusions.map((element) => (
          <TextBoxSelection
            key={element.id}
            textBoxSelectionType={element.type}
            label={element.optionName}
            checked={element.isChecked}
            onSelectionChange={(checked) =>
              handleSelectionChange<TextBoxSelectionData>(
                setData,
                checked,
                "inclusions",
                element.id,
                element.duplicates,
              )
            }
            onPriceChange={(e) =>
              handlePriceChange(setData, element.id, e, "inclusions")
            }
            onNameChange={(e) =>
              handleNameChange(setData, element.id, e, "inclusions")
            }
            price={element.price}
            name={element.enteredName}
            placeHolder={placeholderLabel(t, element.placeholder)}
          />
        ))}
      </WizardSection>

      <WizardSection title={t("wizard.installation.add_ons")}>
        {data.addOnServices.map((element) => (
          <TextBoxSelection
            key={element.id}
            textBoxSelectionType={element.type}
            label={element.optionName}
            checked={element.isChecked}
            onSelectionChange={(checked) =>
              handleSelectionChange<TextBoxSelectionData>(
                setData,
                checked,
                "addOnServices",
                element.id,
              )
            }
            onPriceChange={(e) =>
              handlePriceChange(setData, element.id, e, "addOnServices")
            }
            price={element.price}
            placeHolder={placeholderLabel(t, element.placeholder)}
          />
        ))}
      </WizardSection>
    </WizardPageLayout>
  );
};

export default InstallationConfiguration;
